import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Table } from '../components/ui/Table';
import { Modal } from '../components/ui/Modal';
import { Checkbox } from '../components/ui/Checkbox';
import { Badge } from '../components/ui/Badge';
import { Customer } from '../types';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon } from 'lucide-react';
export function Customers() {
  const {
    filteredCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    selectedCompanyId
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
    terms_accepted: false
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Filter customers by search
  const filteredBySearch = filteredCustomers.filter(customer => customer.name.toLowerCase().includes(searchQuery.toLowerCase()) || customer.email.toLowerCase().includes(searchQuery.toLowerCase()) || customer.phone.includes(searchQuery));
  const openCreateModal = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      email: '',
      address: '',
      phone: '',
      terms_accepted: false
    });
    setFormErrors({});
    setIsModalOpen(true);
  };
  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email,
      address: customer.address,
      phone: customer.phone,
      terms_accepted: customer.terms_accepted
    });
    setFormErrors({});
    setIsModalOpen(true);
  };
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone is required';
    }
    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, formData);
      } else {
        await createCustomer({
          ...formData,
          company_id: selectedCompanyId
        });
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to save customer:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsLoading(true);
    try {
      await deleteCustomer(id);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete customer:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const columns = [{
    key: 'name',
    header: 'Name',
    render: (customer: Customer) => <span className="font-medium text-gray-900" data-id="element-236">{customer.name}</span>
  }, {
    key: 'email',
    header: 'Email',
    render: (customer: Customer) => <a href={`mailto:${customer.email}`} className="text-blue-600 hover:text-blue-800" data-id="element-237">
          {customer.email}
        </a>
  }, {
    key: 'phone',
    header: 'Phone'
  }, {
    key: 'terms',
    header: 'Terms',
    render: (customer: Customer) => <Badge variant={customer.terms_accepted ? 'success' : 'warning'} data-id="element-238">
          {customer.terms_accepted ? 'Accepted' : 'Pending'}
        </Badge>
  }, {
    key: 'actions',
    header: '',
    render: (customer: Customer) => <div className="flex items-center justify-end gap-2" data-id="element-239">
          <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        openEditModal(customer);
      }} aria-label="Edit customer" data-id="element-240">
            <EditIcon className="w-4 h-4" data-id="element-241" />
          </Button>
          <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        setDeleteConfirmId(customer.id);
      }} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Delete customer" data-id="element-242">
            <TrashIcon className="w-4 h-4" data-id="element-243" />
          </Button>
        </div>
  }];
  return <div className="space-y-6" data-id="element-244">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-245">
        <div data-id="element-246">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-247">Customers</h1>
          <p className="text-gray-500 mt-1" data-id="element-248">Manage your customer database</p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-250" />} onClick={openCreateModal} data-id="element-249">
          Add Customer
        </Button>
      </div>

      {/* Search */}
      <Card data-id="element-251">
        <div className="relative" data-id="element-252">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-253" />
          <Input placeholder="Search customers by name, email, or phone..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-254" />
        </div>
      </Card>

      {/* Table */}
      <Table columns={columns} data={filteredBySearch} keyExtractor={c => c.id} emptyMessage="No customers found" data-id="element-255" />

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingCustomer ? 'Edit Customer' : 'Add Customer'} data-id="element-256">
        <div className="space-y-4" data-id="element-257">
          <Input label="Name" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} error={formErrors.name} placeholder="John Doe" data-id="element-258" />
          <Input label="Email" type="email" value={formData.email} onChange={e => setFormData({
          ...formData,
          email: e.target.value
        })} error={formErrors.email} placeholder="john@example.com" data-id="element-259" />
          <Input label="Phone" type="tel" value={formData.phone} onChange={e => setFormData({
          ...formData,
          phone: e.target.value
        })} error={formErrors.phone} placeholder="555-0100" data-id="element-260" />
          <Input label="Address" value={formData.address} onChange={e => setFormData({
          ...formData,
          address: e.target.value
        })} error={formErrors.address} placeholder="123 Main St, Detroit, MI 48201" data-id="element-261" />
          <Checkbox label="Terms accepted" checked={formData.terms_accepted} onChange={e => setFormData({
          ...formData,
          terms_accepted: e.target.checked
        })} data-id="element-262" />

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-263">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-264">
              Cancel
            </Button>
            <Button onClick={handleSubmit} isLoading={isLoading} data-id="element-265">
              {editingCustomer ? 'Save Changes' : 'Add Customer'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} title="Delete Customer" size="sm" data-id="element-266">
        <p className="text-gray-600 mb-6" data-id="element-267">
          Are you sure you want to delete this customer? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-3" data-id="element-268">
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)} data-id="element-269">
            Cancel
          </Button>
          <Button variant="danger" onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)} isLoading={isLoading} data-id="element-270">
            Delete
          </Button>
        </div>
      </Modal>
    </div>;
}